import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import StatusPill from '../components/StatusPill';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
    ShoppingBag, IndianRupee, AlertTriangle, UserCheck, HeartHandshake, Package, Users, TrendingUp,
} from 'lucide-react';

function Kpi({ accent = 'green', label, value, sub, icon: Icon, testid, onClick }) {
    return (
        <div className={`kpi-card accent-${accent} cursor-pointer hover:shadow-md`} onClick={onClick} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase font-semibold text-neutral-500">{label}</div>
                    <div className="text-2xl md:text-3xl font-semibold mt-2 tabular-nums">{value}</div>
                    {sub && <div className="text-xs text-neutral-600 mt-1">{sub}</div>}
                </div>
                {Icon && (
                    <div className="h-9 w-9 rounded-lg bg-neutral-100 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-neutral-700" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backup, setBackup] = useState(null);

    useEffect(() => {
        api.get('/dashboard/summary').then(r => setData(r.data)).finally(() => setLoading(false));
        api.get('/system/status').then(r => setBackup(r.data)).catch(() => setBackup(null));
    }, []);

    return (
        <AppShell pageTitle="Dashboard">
            {loading || !data ? <LoadingBlock /> : (
                <div className="space-y-6">
                    {backup && (
                        <div
                            data-testid="dashboard-backup-notif"
                            onClick={() => nav('/dharmikdashboard/system')}
                            role="button"
                            className={`cursor-pointer flex items-center gap-3 rounded-lg border p-3 ${backup.is_healthy ? 'bg-green-50 border-green-200 text-green-900' : (backup.last_backup ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-900')}`}
                        >
                            <span className={`h-2 w-2 rounded-full ${backup.is_healthy ? 'bg-green-500' : (backup.last_backup ? 'bg-amber-500' : 'bg-red-500')} animate-pulse`} />
                            <div className="flex-1 text-sm">
                                <strong>DB Backup:</strong>{' '}
                                {backup.last_backup ? (
                                    <>Last backup {backup.hours_since_last_backup?.toFixed(1)}h ago ({backup.last_backup.file_name}) · {backup.successful_backups} stored · Next auto backup {new Date(backup.next_scheduled_at).toLocaleString()}</>
                                ) : (
                                    <>No backup yet — click to create one</>
                                )}
                            </div>
                            <span className="text-xs opacity-70">Open System →</span>
                        </div>
                    )}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi accent="blue" label="Orders Today" value={data.orders_today} sub={`Total: ${data.orders_total}`} icon={ShoppingBag} testid="kpi-orders-today" onClick={() => nav('/dharmikdashboard/orders')} />
                        <Kpi accent="green" label="Revenue Today" value={`\u20B9${(data.revenue_today || 0).toLocaleString('en-IN')}`} sub={`Lifetime: \u20B9${(data.revenue_total || 0).toLocaleString('en-IN')}`} icon={IndianRupee} testid="kpi-revenue" />
                        <Kpi accent="amber" label="Pending Wholesale" value={data.pending_wholesale} sub="Approval required" icon={UserCheck} testid="kpi-pending-wholesale-approvals" onClick={() => nav('/dharmikdashboard/wholesale')} />
                        <Kpi accent="red" label="Low Stock Alerts" value={data.low_stock} sub="Items to restock" icon={AlertTriangle} testid="kpi-low-stock-alerts" onClick={() => nav('/dharmikdashboard/inventory')} />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi accent="green" label="Products" value={data.total_products} icon={Package} />
                        <Kpi accent="blue" label="Retail Customers" value={data.total_retail} icon={Users} />
                        <Kpi accent="blue" label="Wholesale Customers" value={data.total_wholesale} icon={UserCheck} />
                        <Kpi accent="amber" label="New Service Enquiries" value={data.new_enquiries} icon={HeartHandshake} onClick={() => nav('/dharmikdashboard/service-enquiries')} />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi accent="blue" label="CRM Leads" value={data.crm_total || 0} icon={ShoppingBag} onClick={() => nav('/dharmikdashboard/crm')} testid="kpi-crm-total" />
                        <Kpi accent="amber" label="Today’s Follow-ups" value={data.crm_today_followups || 0} icon={ShoppingBag} onClick={() => nav('/dharmikdashboard/crm')} testid="kpi-crm-today" />
                        <Kpi accent="red" label="Overdue Follow-ups" value={data.crm_overdue || 0} icon={AlertTriangle} onClick={() => nav('/dharmikdashboard/crm')} testid="kpi-crm-overdue" />
                        <div />
                    </div>

                    <Card className="p-4 sm:p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-semibold">Revenue &amp; Orders (last 7 days)</div>
                                <div className="text-xs text-neutral-500">Paid orders trend</div>
                            </div>
                            <TrendingUp className="h-4 w-4 text-neutral-500" />
                        </div>
                        <div style={{ height: 220 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="revenue" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="orders" stroke="hsl(226 74% 43%)" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card className="p-4 sm:p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-semibold">Recent Orders</div>
                            <Button variant="ghost" size="sm" onClick={() => nav('/dharmikdashboard/orders')} data-testid="view-all-orders-button">View all</Button>
                        </div>
                        {(!data.recent_orders || data.recent_orders.length === 0) ? (
                            <EmptyState icon={ShoppingBag} title="No orders yet" description="Once customers place orders, they will appear here." />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="dashboard-recent-orders-table">
                                    <thead>
                                        <tr className="text-xs text-neutral-500 border-b">
                                            <th className="text-left font-medium py-2">Order #</th>
                                            <th className="text-left font-medium">Customer</th>
                                            <th className="text-left font-medium">Status</th>
                                            <th className="text-left font-medium">Payment</th>
                                            <th className="text-right font-medium">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.recent_orders.map(o => (
                                            <tr key={o.id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={() => nav(`/dharmikdashboard/orders/${o.id}`)}>
                                                <td className="py-3 font-mono text-xs">{o.order_number}</td>
                                                <td>{o.customer_snapshot?.name}</td>
                                                <td><StatusPill status={o.status} /></td>
                                                <td><StatusPill status={o.payment_status} /></td>
                                                <td className="text-right tabular-nums">\u20B9{o.totals?.grand_total?.toLocaleString('en-IN')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </AppShell>
    );
}
